// DocumentsReceived.jsx
import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Card, CardBody, Badge, Table, Label } from 'reactstrap';
import { Select } from 'components/Common';
import { StyledDatePicker } from 'components/CustomDatepicker';
import i18n from 'i18n';

export const DOCS_RECEIVED_BY_CHOICES = [
  { label: i18n.t('order.email'), value: 'email' },
  { label: i18n.t('order.personal'), value: 'personal' },
  { label: i18n.t('order.wizard'), value: 'wizard' },
  { label: i18n.t('order.post'), value: 'post' },
  { label: i18n.t('order.courierDelivery'), value: 'courier_delivery' },
  { label: i18n.t('order.courierPickup'), value: 'courier_pickup' },
  { label: i18n.t('order.vsUPSLabel'), value: 'vs_ups_label' },
];

export const DocumentsReceived = forwardRef(
  (
    {
      index,
      currentApplicant,
      // isDisabledFields,
      // isApplicantInWork,
      formik,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [docsReceivedHistoryisOpen, setDocsReceivedHistoryisOpen] =
      useState(false);

    // Store the original value when component mounts or after successful save
    const originalDocumentsReceivedByRef = useRef(
      currentApplicant?.documentsReceivedBy,
    );

    const documentsReceiptHistory = currentApplicant?.documentsReceivedHistory;
    const latestRecord =
      documentsReceiptHistory?.[documentsReceiptHistory.length - 1];

    // Detect when documents_received_by has changed from its original value
    const hasDocumentsReceivedByChanged =
      !!formik.values.orderDetail?.number &&
      originalDocumentsReceivedByRef.current !==
        currentApplicant?.documentsReceivedBy;

    // Reset note when method changes - DO NOT update the ref here
    useEffect(() => {
      if (hasDocumentsReceivedByChanged) {
        formik.setFieldValue(`applicants[${index}].documentsReceivedNote`, '');
      }
    }, [currentApplicant?.documentsReceivedBy]);

    const updateOriginalValue = () => {
      originalDocumentsReceivedByRef.current =
        currentApplicant?.documentsReceivedBy;
    };

    useImperativeHandle(ref, () => ({
      updateOriginalValue: updateOriginalValue,
    }));

    return (
      <>
        {/* Documents received selectors */}
        <Row className="mb-4 align-items-center">
          <Col xs={12} lg={2}>
            <Label className="display-5 m-0">
              {t('order.documentsReceivedBy')}
            </Label>
          </Col>

          <Col xs={12} lg={4}>
            <Select
              classNamePrefix="react-select"
              options={DOCS_RECEIVED_BY_CHOICES}
              // isDisabled={isDisabledFields || isApplicantInWork}
              value={DOCS_RECEIVED_BY_CHOICES.find(
                item => item.value === currentApplicant?.documentsReceivedBy,
              )}
              onChange={event => {
                const newValue = event?.value;
                formik.setFieldValue(
                  `applicants[${index}].documentsReceivedBy`,
                  newValue,
                );
              }}
            />
          </Col>

          <Col xs={12} lg={2} className="d-flex align-items-center">
            <Label className="display-5 m-0">
              {t('order.documentsReceivedOn')}
            </Label>
          </Col>

          <Col xs={12} lg={4}>
            <StyledDatePicker
              onChange={date =>
                formik.setFieldValue(
                  `applicants[${index}].documentsReceivedOn`,
                  date,
                )
              }
              selected={currentApplicant?.documentsReceivedOn}
              placeholder={t('common.datePlaceholder')}
              placeholderText={t('common.datePlaceholder')}
              invalid={formik.errors?.applicants?.[index]?.documentsReceivedOn}
            />
          </Col>
        </Row>

        {/* Documents Received Note - Only show when method has changed from original */}
        {hasDocumentsReceivedByChanged && (
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="border-info bg-light">
                <CardBody>
                  <Label className="fw-semibold mb-2">
                    <i className="bx bx-note me-1" />
                    {t('order.documentsReceivedNote')}
                  </Label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={currentApplicant?.documentsReceivedNote || ''}
                    onChange={e => {
                      formik.setFieldValue(
                        `applicants[${index}].documentsReceivedNote`,
                        e.target.value,
                      );
                    }}
                    placeholder={t('order.documentsReceivedNotePlaceholder')}
                    style={{ resize: 'vertical' }}
                    // disabled={isDisabledFields || isApplicantInWork}
                    autoFocus
                  />
                  <div className="mt-2">
                    <small className="text-muted d-block mb-2">
                      {t('order.suggestedText')}:
                    </small>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => {
                        const currentNote =
                          currentApplicant?.documentsReceivedNote || '';
                        const suggestedText = 'Original passport received';
                        const newNote = currentNote
                          ? `${currentNote}\n${suggestedText}`
                          : suggestedText;
                        formik.setFieldValue(
                          `applicants[${index}].documentsReceivedNote`,
                          newNote,
                        );
                      }}
                    >
                      <i className="bx bx-plus-circle me-1" />
                      Original passport received
                    </button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Documents received history */}
        {!!formik.values.orderDetail?.number && (
          <Row className="mb-4">
            <Col xs={12}>
              <Label className="display-6 mb-3 fw-semibold">
                {t('order.documentsReceivedHistory')}
              </Label>

              {/* Latest Update Card */}
              {latestRecord && (
                <Card className="mb-4 border-primary border-2 bg-light">
                  <CardBody className="py-3">
                    <Row className="align-items-start">
                      <Col xs={12} md={3}>
                        <div className="d-flex align-items-center">
                          <i className="bx bx-bell-ring text-primary fs-4 me-2" />
                          <div>
                            <small className="text-muted d-block">
                              {t('order.dateAndTime')}
                            </small>
                            <strong className="text-primary">
                              {latestRecord.changedAt}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col xs={12} md={9}>
                        <div className="d-flex flex-wrap gap-4">
                          <div style={{ minWidth: '100px' }}>
                            <small className="text-muted d-block">
                              {t('order.by')}
                            </small>
                            <strong>
                              {latestRecord.changedBy || 'System'}
                            </strong>
                          </div>

                          <div style={{ minWidth: '150px' }}>
                            <small className="text-muted d-block">
                              {t('order.receiptDate')}
                            </small>
                            <div className="d-flex gap-2">
                              <span className="text-decoration-line-through">
                                {latestRecord.date?.old ?? '—'}
                              </span>
                              <i className="bx bx-arrow-right" />
                              <span className="fw-bold text-success">
                                {latestRecord.date?.new ?? '—'}
                              </span>
                            </div>
                          </div>

                          <div style={{ minWidth: '160px' }}>
                            <small className="text-muted d-block">
                              {t('order.receiptMethod')}
                            </small>
                            <div className="d-flex gap-2">
                              <span className="text-decoration-line-through">
                                {latestRecord.method?.oldLabel ?? '—'}
                              </span>
                              <i className="bx bx-arrow-right" />
                              <span className="fw-bold text-primary">
                                {latestRecord.method?.newLabel ?? '—'}
                              </span>
                            </div>
                          </div>

                          {/* Note in latest card - FIXED */}
                          <div style={{ minWidth: '200px', flex: 1 }}>
                            <small className="text-muted d-block">
                              {t('order.documentsReceivedNote')}
                            </small>
                            <div className="d-flex gap-2">
                              {latestRecord.note?.old &&
                                latestRecord.note?.old !== '—' && (
                                  <>
                                    <span className="text-decoration-line-through">
                                      {latestRecord.note?.old}
                                    </span>
                                    <i className="bx bx-arrow-right" />
                                  </>
                                )}
                              <span className="fw-bold text-warning">
                                {latestRecord.note?.new ||
                                  latestRecord.note ||
                                  '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* No History State */}
              {(!documentsReceiptHistory ||
                documentsReceiptHistory.length === 0) && (
                <Card className="bg-light border-0">
                  <CardBody className="text-center py-4">
                    <i className="bx bx-history me-2" />
                    <span className="text-muted">
                      {t('order.noHistoryAvailable')}
                    </span>
                  </CardBody>
                </Card>
              )}

              {/* Previous Changes Accordion */}
              {documentsReceiptHistory?.length > 1 && (
                <Card className="border-0">
                  <div
                    onClick={() =>
                      setDocsReceivedHistoryisOpen(!docsReceivedHistoryisOpen)
                    }
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>
                      <i className="bx bx-history me-2" />
                      <strong>{t('order.previousChanges')}</strong>
                      <span className="text-muted ms-2">
                        ({documentsReceiptHistory.length - 1}{' '}
                        {documentsReceiptHistory.length - 1 === 1
                          ? t('order.entry')
                          : t('order.entries')}
                        )
                      </span>
                    </span>
                    <i
                      className={`bx bx-chevron-${
                        docsReceivedHistoryisOpen ? 'up' : 'down'
                      } fs-4`}
                    />
                  </div>

                  {docsReceivedHistoryisOpen && (
                    <div className="mt-3">
                      <div className="table-responsive">
                        <Table
                          hover
                          className="align-middle"
                          style={{ minWidth: '900px' }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '18%' }}>
                                <i className="bx bx-time me-1" />{' '}
                                {t('order.dateAndTime')}
                              </th>
                              <th style={{ width: '12%' }}>
                                <i className="bx bx-user me-1" />{' '}
                                {t('order.user')}
                              </th>
                              <th style={{ width: '20%' }}>
                                <i className="bx bx-calendar me-1" />{' '}
                                {t('order.receiptDate')}
                              </th>
                              <th style={{ width: '25%' }}>
                                <i className="bx bx-envelope me-1" />{' '}
                                {t('order.receiptMethod')}
                              </th>
                              <th style={{ width: '25%' }}>
                                <i className="bx bx-edit-alt me-1" />{' '}
                                {t('order.documentsReceivedNote')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentsReceiptHistory
                              .slice(0, -1)
                              .map((historyItem, i) => (
                                <tr key={i}>
                                  <td className="text-nowrap">
                                    {historyItem.changedAt}
                                  </td>
                                  <td>{historyItem.changedBy || 'System'}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-1">
                                      <Badge
                                        color="secondary"
                                        className="text-nowrap"
                                      >
                                        {historyItem.date?.old ?? '—'}
                                      </Badge>
                                      <i className="bx bx-right-arrow-alt" />
                                      <Badge
                                        color="success"
                                        className="text-nowrap"
                                      >
                                        {historyItem.date?.new ?? '—'}
                                      </Badge>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-1">
                                      <Badge
                                        color="light"
                                        className="text-dark text-nowrap"
                                      >
                                        {historyItem.method?.oldLabel ?? '—'}
                                      </Badge>
                                      <i className="bx bx-right-arrow-alt" />
                                      <Badge
                                        color="primary"
                                        className="text-nowrap"
                                      >
                                        {historyItem.method?.newLabel ?? '—'}
                                      </Badge>
                                    </div>
                                  </td>
                                  <td
                                    className="text-wrap"
                                    style={{ wordBreak: 'break-word' }}
                                  >
                                    {historyItem.note?.new ||
                                      historyItem.note ||
                                      '—'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </Col>
          </Row>
        )}
      </>
    );
  },
);
